import { getPerformanceFromTable } from '../util/performance-table-utility'
import { PreCalculationResults } from './pre-calculation'
import { PerformanceTableFragment } from '../../../generated/graphql'
import { calculateUsedCapacity, HeatingHeatPumpResults } from './heat-pump-util'

export type CirculationCalculationParams = {
  performanceTable: PerformanceTableFragment
  heatRecycleAvailable: boolean
  copModifier: number
  heatingPower: number
  preCalculationResults: PreCalculationResults
}

export type CirculationCalculationResults = {
  heating: HeatingHeatPumpResults
  cooling: {
    totalDemand: number
  }
  powerCapacity: number
}

function circulate(
  heatingPower: number,
  cop: number,
  powerCoefficient: number,
  heatingDemand: number,
  coolingDemand: number,
  powerCapacity: number,
) {
  if (heatingDemand === 0 || coolingDemand === 0) {
    return {
      heatingDemand,
      coolingDemand,
      circulationPower: 0,
      powerCapacity,
      cop,
    }
  }

  const circulationPower = Math.min(
    heatingDemand,
    coolingDemand * (1 + 1 / (cop - 1)),
    heatingPower * powerCoefficient * powerCapacity,
  )

  const usedCapacity = calculateUsedCapacity(
    circulationPower,
    heatingPower,
    powerCoefficient,
  )

  return {
    heatingDemand: heatingDemand - circulationPower,
    coolingDemand: coolingDemand - circulationPower * (1 - 1 / cop),
    circulationPower,
    powerCapacity: powerCapacity - usedCapacity,
  }
}

export function calculateCirculationForHour({
  performanceTable,
  heatRecycleAvailable,
  copModifier,
  heatingPower,
  preCalculationResults,
}: CirculationCalculationParams): CirculationCalculationResults {
  const { heating, cooling } = preCalculationResults

  if (!heatRecycleAvailable || !heating || !cooling) {
    return {
      heating: {
        basic: {
          totalDemand: heating?.basic.totalDemand ?? 0,
          providedHeating: 0,
          cop: NaN,
        },
        dhw: {
          totalDemand: heating?.dhw.totalDemand ?? 0,
          providedHeating: 0,
          cop: NaN,
        },
      },
      cooling: {
        totalDemand: cooling?.totalDemand ?? 0,
      },
      powerCapacity: 1,
    }
  }

  // Get COPs and power coefficients from performance tables

  const basicPerformance = getPerformanceFromTable(
    performanceTable,
    cooling.temperature,
    heating.basic.temperature,
    copModifier,
  )
  const dhwPerformance = getPerformanceFromTable(
    performanceTable,
    cooling.temperature,
    heating.dhw.temperature,
    copModifier,
  )

  // Calculate basic circulation

  const {
    heatingDemand: basicHeatingDemand,
    coolingDemand: coolingDemandAfterBasicCirculation,
    circulationPower: basicCirculationPower,
    powerCapacity: powerCapacityAfterBasicCirculation,
  } = circulate(
    heatingPower,
    basicPerformance.cop,
    basicPerformance.powerCoefficient,
    heating.basic.totalDemand,
    cooling.totalDemand,
    1,
  )

  // Calculate DHW circulation

  const {
    heatingDemand: dhwHeatingDemand,
    coolingDemand: coolingDemandAfterDhwCirculation,
    circulationPower: dhwCirculationPower,
    powerCapacity: powerCapacityAfterDhwCirculation,
  } = circulate(
    heatingPower,
    dhwPerformance.cop,
    dhwPerformance.powerCoefficient,
    heating.dhw.totalDemand,
    coolingDemandAfterBasicCirculation,
    powerCapacityAfterBasicCirculation,
  )

  return {
    heating: {
      basic: {
        totalDemand: basicHeatingDemand,
        providedHeating: basicCirculationPower,
        cop: basicPerformance.cop,
      },
      dhw: {
        totalDemand: dhwHeatingDemand,
        providedHeating: dhwCirculationPower,
        cop: dhwPerformance.cop,
      },
    },
    cooling: {
      totalDemand: coolingDemandAfterDhwCirculation,
    },
    powerCapacity: powerCapacityAfterDhwCirculation,
  }
}
